import { useUsersStore } from "~/store/users/users.store";
import LearningPathAttendeesTab from "~/components/Pages/learning-path/component/attendees/AttendeesTab.vue";
import LearningPathTrainingsTab from "~/components/Pages/learning-path/component/attendees/TrainingsTab.vue";
import { LearningPathAttendeesNavEnum, LearningPathAttendeesTabTypeEnum } from "~/utils/enums/learning-path.enums";
import type { IHeaderNav, IIndexHeader } from "~/utils/interfaces/table-interfaces";
import { useLearningPathAttendeesStore } from "~/store/learning-path/learning-path-attendees.store";
import type { IFooterActions } from "~/utils/interfaces/common-interfaces";
import { BookingStatusEnum } from "~/utils/enums/bookings-events.enums";

export const useLearningPathAttendees = () => {
  const { handleLearningPathDeleteAttendeesModal } = useLearningPathAttendeesStore();
  function getNav(): IHeaderNav[] {
    return [];
  }

  function getLearningPathAttendeesNav(): IHeaderNav[] {
    const nav = [
      {
        name: LearningPathAttendeesNavEnum.ATTENDEES,
        type: LearningPathAttendeesTabTypeEnum.ATTENDEES,
        component: LearningPathAttendeesTab,
      },
      {
        name: LearningPathAttendeesNavEnum.TRAININGS,
        type: LearningPathAttendeesTabTypeEnum.TRAININGS,
        component: LearningPathTrainingsTab,
      },
    ];

    return nav;
  }

  function findLearningPathNav(type: LearningPathAttendeesTabTypeEnum) {
    return getLearningPathAttendeesNav().find((nav) => nav.type === type);
  }

  async function fetchNotExcludedUserLearningPath(
    userIdList: string[],
    context?: string,
    enableRolesFilters: boolean = true,
    paginateOptions?: Record<string, any>,
  ) {
    const usersStore = useUsersStore();
    // `null` means the store already surfaced the rejection.
    return (
      (await usersStore.fetchUsersNotExcluded({
        excludedUserIds: userIdList,
        context,
        enableRolesFilters,
        paginateOptions,
      })) ?? undefined
    );
  }

  const headers = useState<IIndexHeader[]>(() => [
    {
      name: "Nom",
      sort: true,
      key: "displayValue",
      minWidthPx: 180,
      maxWidthFr: 1,
      visible: true,
    },
    {
      name: "Email",
      sort: true,
      key: "email",
      minWidthPx: 327,
      maxWidthFr: 1,
      visible: true,
    },
    {
      name: "Date d'ajout",
      sort: true,
      key: "created_at",
      minWidthPx: 140,
      maxWidthFr: 1,
      visible: true,
    },
    {
      name: "Entité légale",
      sort: true,
      key: "legal_entity_name",
      minWidthPx: 140,
      maxWidthFr: 1,
      visible: true,
    },
    {
      name: "Complétion",
      sort: true,
      key: "completion_rate",
      minWidthPx: 140,
      maxWidthFr: 1,
      visible: true,
    },
    {
      name: "Durée",
      sort: true,
      key: "duration_hours",
      minWidthPx: 100,
      maxWidthFr: 1,
      visible: true,
    },
    {
      name: "Score",
      sort: true,
      key: "score",
      minWidthPx: 100,
      maxWidthFr: 1,
      visible: true,
      tooltip: `Moyenne des résultats de chaque formation du parcours. <br/> 
      Les formations qui n'ont pas de résultat sont exclues.`,
    },
  ]);

  const headersJourney = useState<IIndexHeader[]>(() => [
    {
      name: "Formation",
      sort: true,
      key: "training_name",
      minWidthPx: 180,
      maxWidthFr: 1,
      visible: true,
    },
    {
      name: "Statut",
      sort: true,
      key: "status",
      minWidthPx: 180,
      maxWidthFr: 1,
      visible: true,
    },
    {
      name: "Date de début",
      sort: true,
      key: "start_date",
      minWidthPx: 180,
      maxWidthFr: 1,
      visible: true,
    },
    {
      name: "Complétion",
      sort: true,
      key: "completion_rate",
      minWidthPx: 180,
      maxWidthFr: 1,
      visible: true,
    },
    {
      name: "Durée",
      sort: true,
      key: "duration_hours",
      minWidthPx: 180,
      maxWidthFr: 1,
      visible: true,
    },
    {
      name: "Score",
      sort: true,
      key: "score",
      minWidthPx: 100,
      maxWidthFr: 1,
      visible: true,
    },
  ]);

  const footersActionsAttendees: IFooterActions[] = [
    {
      name: "Supprimer",
      logoName: "delete_trash",
      action: () => handleLearningPathDeleteAttendeesModal(true),
      cancelColor: true,
    },
  ];

  function statusMapping(value: string): { name: string; value: string; type: string } | undefined {
    return [
      {
        name: BookingStatusEnum.BOOKING_TO_BE_CONFIRMED,
        value: "Invitation envoyée",
        type: "light_orange",
      },
      {
        name: BookingStatusEnum.BOOKING_CONFIRMED,
        value: "Inscrit",
        type: "light_green",
      },
      {
        name: BookingStatusEnum.BOOKING_TRAINING_STARTED,
        value: "Formation démarrée",
        type: "light_blue",
      },
      {
        name: BookingStatusEnum.BOOKING_TRAINING_ENDED,
        value: "Terminée",
        type: "green",
      },
      {
        name: BookingStatusEnum.BOOKING_ON_HOLD,
        value: "En attente",
        type: "grey",
      },
      {
        name: BookingStatusEnum.BOOKING_CANCELED,
        value: "Annulé",
        type: "light_red",
      },
      {
        name: BookingStatusEnum.BOOKING_REFUSED,
        value: "Refusé",
        type: "light_red",
      },
      {
        name: BookingStatusEnum.BOOKING_MISSING_PROOFS,
        value: "Justificatifs manquants",
        type: "light_orange",
      },
      {
        name: BookingStatusEnum.BOOKING_BILLABLE,
        value: "Facturable",
        type: "light_purple",
      },
      {
        name: BookingStatusEnum.BOOKING_INCOMPLETE,
        value: "Incomplet",
        type: "light_orange",
      },
      {
        name: BookingStatusEnum.BOOKING_NO_SHOW,
        value: "Absent",
        type: "grey",
      },
      {
        name: BookingStatusEnum.BOOKING_REMOVED,
        value: "Supprimé",
        type: "light_grey",
      },
    ].find((s) => s.name === value);
  }

  return {
    getLearningPathAttendeesNav,
    findLearningPathNav,
    fetchNotExcludedUserLearningPath,
    headers,
    headersJourney,
    getNav,
    footersActionsAttendees,
    statusMapping,
  };
};
